/* eslint-disable max-lines */
import {
    ChatAltIcon,
    InformationCircleIcon,
} from '../../Icon'
import {
    ModalContent,
    ModalFooter,
    ModalFooterLeft,
    ModalHeader,
} from '../ModalView'
import {
    NinchatCannedMessage, NinchatCannedMessageLanguages,
} from 'api/types/properties'
import {
    SubmitHandler,
    useForm,
} from 'react-hook-form'
import {
    styledFieldset,
    styledInput,
    styledLabel,
    styledSmall,
    styledTextarea,
} from '../../Form/styles/FormView'
import Button from '../../Button'
import {EditCannedMessageModalViewProps} from './types'
import {Icon} from 'components/Icon/types'
import Modal from '../Modal'
import React from 'react'
import Toggle from 'components/Toggle'
import {isArrayEqual} from 'utils/props'
import {styled} from '../../../stitches/config'
import {translate} from 'utils/cannedmessages'
import {useTranslation} from 'react-i18next'

const StyledForm = styled('form')
const StyledFieldset = styled('fieldset', styledFieldset, {
    display: 'flex',
    flexDirection: 'column',
})
const StyledInput = styled('input', styledInput)
const StyledLabel = styled('label', styledLabel)
const StyledSmall = styled('small', styledSmall, {display: 'block'})
const StyledTextarea = styled('textarea', styledTextarea, {
    flexGrow: 1,
    minHeight: '120px',
})

type FormValues = {
    keyword: string,
    message: string,
    translations: {
        [K in NinchatCannedMessageLanguages[number]['id']]?: string
    },
}

const EditCannedMessageModalView = (props: EditCannedMessageModalViewProps): React.ReactElement => {
    const {
        cannedMessages,
        supportedCannedMessageLanguages,
        keyword,
        message,
        saveAvailable,
        onCancel,
        onSave,
    } = props

    const {t} = useTranslation()

    const cannedMessage = keyword ? cannedMessages.find((_message) => _message.keywords[0] === keyword) : undefined
    const currentKeyword = cannedMessage ? cannedMessage.keywords[0] : keyword ? keyword : undefined
    const currentMessage = cannedMessage ? cannedMessage.message : message ? message : undefined
    const currentTranslations = cannedMessage?.translations ?? undefined
    const prevTranslations = React.useRef(Object.values(currentTranslations ?? {}))

    const defaultValues = {
        keyword: currentKeyword,
        message: currentMessage,
        translations: currentTranslations,
    }

    const {
        formState: {errors},
        handleSubmit,
        register,
        reset,
    } = useForm<FormValues>({
        defaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    })

    React.useEffect(() => {
        if (!isArrayEqual(prevTranslations.current, Object.values(currentTranslations ?? {}))) {
            prevTranslations.current = Object.values(currentTranslations ?? {})
            reset({
                keyword: currentKeyword,
                message: currentMessage,
                translations: currentTranslations,
            })
        }
    }, [currentTranslations])


    const isUnique = (key: string) => {
        if (keyword && keyword === key) {
            // We are editing existing message, and we haven't changed the keyword
            return true
        }

        return !cannedMessages.find((_message) => _message.keywords[0] === key)
    }

    const parentExists = (key: string) => {
        const lastIndex = key.lastIndexOf('/')
        const parentKey = key.slice(0, lastIndex)

        if (lastIndex === -1) {
            return true
        }

        return !!cannedMessages.find((_message) => _message.keywords[0] === parentKey)
    }

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        // A little trick to convert empty strings to null values
        const finalData = JSON.parse(JSON.stringify(data, (key, value) => (key && value === '' ? null : value)))

        if (keyword) {
            // We may be editing existing canned message
            let foundOriginal = false

            const newCannedMessages: NinchatCannedMessage[] = cannedMessages.map((_message) => {
                if (_message.keywords[0] === keyword) {
                    foundOriginal = true

                    return {
                        keywords: [finalData.keyword],
                        message: finalData.message,
                        translations: finalData.translations,
                        ...finalData.en && {en: finalData.en},
                        ...finalData.sv && {sv: finalData.sv},
                    }
                } else if (_message.keywords[0].indexOf(`${keyword}/`) === 0) {
                    // Check if the keyword is a childred of a changed key (= sub-category)
                    return {
                        keywords: [_message.keywords[0].replace(`${keyword}/`, `${finalData.keyword}/`)],
                        message: _message.message,
                        translations: _message.translations,
                        ..._message.en && {en: _message.en},
                        ..._message.sv && {sv: _message.sv},
                    }
                }

                // Pick only keywords and message (slowly clean up eg. $$hashKey added by Angular)
                return {
                    keywords: _message.keywords,
                    message: _message.message,
                    translations: _message.translations,
                    ..._message.en && {en: _message.en},
                    ..._message.sv && {sv: _message.sv},
                }
            })

            if (!foundOriginal) {
                // Original keyword was not found, hence we are dealing with a new canned message with pre-defined keyword

                newCannedMessages.push({
                    keywords: [finalData.keyword],
                    message: finalData.message,
                    translations: finalData.translations,
                    ...finalData.en && {en: finalData.en},
                    ...finalData.sv && {sv: finalData.sv},
                })
            }

            onSave(newCannedMessages)
        } else {
            // We are adding a new canned message
            const newCannedMessages: NinchatCannedMessage[] = cannedMessages.concat(
                {
                    keywords: [finalData.keyword],
                    message: finalData.message,
                    translations: finalData.translations,
                    ...finalData.en && {en: finalData.en},
                    ...finalData.sv && {sv: finalData.sv},
                },
            )

            onSave(newCannedMessages)
        }
    }

    return (
        <Modal onClose={onCancel}>
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <ModalHeader icon={ChatAltIcon} title={cannedMessage ? t('Edit canned message') : t('New canned message')} />
                <ModalContent>
                    <StyledFieldset>
                        <StyledLabel htmlFor='keyword'>{t('Keyword and / or category')}</StyledLabel>
                        <StyledInput
                            {...register('keyword', {
                                pattern: {
                                    message: t('Invalid keyword format'),
                                    // (?!\/)     - don't allow / at start
                                    // (?!.*\/$)  - don't allow 2 consecutive /:s
                                    // (?!.*\/\/) - don't allow / at the end
                                    value: /^(?!\/)(?!.*\/$)(?!.*\/\/)[a-z0-9/]+$/iu,
                                },
                                required: t('Keyword is required'),
                                validate: {
                                    isUnique: (value) => isUnique(value) || t('Keyword must be unique'),
                                    parentExists: (value) => parentExists(value) || t('Parent category doesn\'t exist'),
                                },
                            })}
                            aria-invalid={errors.keyword ? 'true' : 'false'}
                            id='keyword'
                            placeholder={t('Add a keyword (and category)')}
                            tabIndex={0}
                            type='text'
                        />
                        {!!errors.keyword && <StyledSmall error={true} role='alert'>{errors.keyword?.message}</StyledSmall>}
                        <StyledSmall>{t('Chain keywords to create categories, E.g. greeting/hello (separate with /).')}</StyledSmall>
                        <StyledLabel htmlFor='message'>{t('Message')}</StyledLabel>
                        {supportedCannedMessageLanguages?.map((language: NinchatCannedMessageLanguages[0]) => (
                            <Toggle
                                // eslint-disable-next-line react/no-unstable-nested-components
                                content={() => (
                                    <StyledTextarea
                                        {...register(`translations.${language.id}`)}
                                        id={`message-${language.id}`}
                                        placeholder={t('Add message text')}
                                        tabIndex={-1}
                                    />
                                )}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                header={() => <StyledLabel htmlFor={`message-${language.id}`}> {translate(language.name, t)}</StyledLabel>}
                                isCollapsedByDefault={false}
                                key={language.id}
                                small={true}
                            />
                        ))}
                    </StyledFieldset>
                </ModalContent>
                <ModalFooter>
                    <ModalFooterLeft>
                        <Button color='text' iconLeft={InformationCircleIcon as Icon} tabIndex={-1} text={t('Help')} />
                    </ModalFooterLeft>
                    <Button onPress={onCancel} tabIndex={-1} text={t('Cancel')} />
                    <Button color='filled' disabled={!saveAvailable} margin='left' tabIndex={-1} text={t('Save')} type='submit' />
                </ModalFooter>
            </StyledForm>
        </Modal>
    )
}

export default EditCannedMessageModalView
